import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/2d534211-f673-4bdf-b323-dbc700b856c8/images/6e0a58f9cc5b71082cfb67ad179c95b30bb2f447735f49bd1f0c81abec5a5c37.png";

export default function StoryPage() {
  return (
    <div data-testid="story-page" className="pb-24">
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 md:pt-24 grid md:grid-cols-12 gap-12 items-end">
        <div className="md:col-span-7">
          <div className="eyebrow mb-5">My Story</div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[0.98] tracking-tight">
            A small workshop. <span className="italic">A long obsession</span>.
          </h1>
        </div>
        <div className="md:col-span-5 text-[#2C2421]/80 leading-relaxed text-lg">
          I'm Hayden — a builder of thirty-two years who picked up the chisels at
          night and never quite put them down. The Woodworkers Shed is where I
          bring old native timber back to life, one piece at a time.
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mt-16">
        <div className="aspect-[16/9] bg-[#F5F2EB] overflow-hidden">
          <img src={HERO_IMG} alt="The workshop" className="w-full h-full object-cover" />
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 md:px-12 mt-24 space-y-10 text-[#2C2421]/85 leading-relaxed text-lg">
        <p>
          My name is Hayden, and The Woodworkers Shed is a one-person workshop
          tucked away in Whangamata, Aotearoa. I've been a builder by trade for
          thirty-two years — and somewhere along the way the offcuts, the
          old beams pulled from villas, and the quiet hours at the bench took
          on a life of their own.
        </p>
        <p>
          Whangamata wasn't an accident. I came here for the lifestyle — the
          ocean, the bush, the slower pace — and stayed because the place keeps
          handing me inspiration. There's a particular kind of light in this
          part of the country that makes you want to take your time over things.
        </p>
        <p>
          I work almost entirely with native and locally-recycled native
          timbers — Rimu, and others on request. Every piece carries a history:
          a beam from a hundred-year-old homestead, a slab someone was about to
          burn, a length of Rimu nobody else wanted. My job is to listen to the
          grain, choose the best face, and bring it back to life as something
          you'll keep in your kitchen for the next fifty years.
        </p>
        <p>
          The workshop is small but properly kitted out — a dimension saw, a
          good thicknesser, a pair of planers I trust, and the kind of hand
          tools that get sharper with age. Nothing leaves the bench until it
          would feel at home in my own kitchen. Everything is finished in
          food-safe hard-wax oils, signed, and sent off with a care card.
        </p>
        <p className="font-serif text-2xl italic text-[#6F4E37]">
          "The best pieces don't shout. They earn their place in the kitchen, on
          the table, and eventually, in the family."
        </p>
        <p>
          This is genuinely a passion — bringing the beauty of recycled native
          timber back to life is what gets me into the workshop early and keeps
          me there late. If you've found something here you like, I'd love to
          make it for you. If you have an idea that isn't on the site, even
          better — the most interesting pieces almost always start as a
          conversation.
        </p>
        <p>
          I also take on{" "}
          <Link to="/gifts" className="link-underline text-[#2C2421] font-medium">
            corporate and gift orders
          </Link>
          {" "}— engraved boards for real estate settlements, Christmas hampers,
          wedding gifts and the like. Minimum ten pieces, with two weeks' lead
          time.
        </p>
        <p className="font-serif text-xl text-[#2C2421]">
          — Hayden
        </p>
      </section>

      <div className="text-center mt-20">
        <Link
          to="/commissions"
          data-testid="story-commission-link"
          className="inline-flex items-center gap-3 bg-[#2C2421] text-[#FDFBF7] px-7 py-4 text-sm font-medium hover:bg-[#4A3C38] transition-colors"
        >
          Start a commission <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}
